import React from "react";
import { format } from "date-fns";
import { connect } from "react-redux";
import { isEqual } from "lodash";
import { bindActionCreators } from "redux";
import { AgGridReact } from "ag-grid-react";
import { Header, Button } from "semantic-ui-react";
import { DatesRangeInput } from "semantic-ui-calendar-react";

import { loadPaymentGridPage, changePaymentGridPage, changePaymentGridState } from "./actions";
import GridPageSizePicker from "../../common/components/grid-page-size-picker";
import { allGridColumns } from "./columns";
import { DateFormat } from "utilities/dist/invoice/constants";
import { formatAmount } from "utilities/dist/helpers";
import { CompanyFeatures } from "utilities/dist/company/constants";
import { isCompanyFeatureActive } from "utilities/dist/company/helpers";

const NoPaymentsOverlay = () => {
    return (
        <div className="tm-20">
            <Header>No payment history found.</Header>
        </div>
    );
};

class DataGrid extends React.Component {
    state = {
        dateRange: "",
    };

    handleDateRangeChange = (e, { value }) => {
        const filterModel = { ...this.props.filterModel };
        const [start, end] = value?.split(" - ");

        if (start && this.grid) {
            filterModel.received_at = {
                type: "inRange",
                filterType: "date",
                dateTo: format(new Date(end || start), "yyyy-MM-dd"),
                dateFrom: format(new Date(start), "yyyy-MM-dd"),
            };
        } else if (filterModel.received_at) {
            delete filterModel["received_at"];
        }

        this.props.changePaymentGridState({ filterModel });
        this.setState({ dateRange: value });
    };

    grid = null;
    gridDataSource = {
        getRows: params => {
            this.props.loadPaymentGridPage(this.props, params);
        },
    };

    componentWillUnmount() {
        this.grid = null;
    }

    onGridReady = grid => {
        this.grid = grid;

        setTimeout(() => this.grid && this.grid.api.sizeColumnsToFit(), 300);

        this.grid.api.setDatasource(this.gridDataSource);
        this.grid.columnApi.applyColumnState({ state: this.props.sortModel, defaultState: {} });
        this.grid.api.setFilterModel(this.props.filterModel);

        this.grid.api.addEventListener("sortChanged", () => {
            const colStates = this.grid.columnApi.getColumnState();
            const sortModel = colStates
                .filter(colState => colState.sort)
                .map(colState => ({
                    colId: colState.colId,
                    sort: colState.sort,
                }));

            this.props.changePaymentGridState({
                sortModel,
            });
        });

        this.grid.api.addEventListener("filterChanged", () => {
            this.props.changePaymentGridState({
                filterModel: this.grid.api.getFilterModel(),
            });
        });
    };

    reloadGridData = () => {
        this.grid && this.grid.api.refreshInfiniteCache();
    };

    changePage = () => {
        if (this.grid) {
            const page = this.grid.api.paginationGetCurrentPage() + 1;

            if (page === this.props.currentPage) return;

            this.props.changePaymentGridPage(page);
        }
    };

    componentDidUpdate(prevProps) {
        if (!this.grid) return;

        if (prevProps.isLoading && !this.props.isLoading) {
            this.grid.api.hideOverlay();
        }

        if (!prevProps.isLoading && this.props.isLoading) {
            this.grid.api.showLoadingOverlay();
        }

        if (!this.props.isLoading && this.props.payments.length < 1) {
            this.grid.api.showNoRowsOverlay();
        }

        if (!isEqual(prevProps.filterModel, this.props.filterModel)) {
            this.grid.api.setFilterModel(this.props.filterModel);
        }
    }

    // Any error happening here can crash the grid so let's catch errors here
    getFooterData = () => {
        try {
            let amount = 0;
            if (!this.grid?.api) return [{ amount }];
            this.grid.api.forEachNode(node => {
                amount += node.data?.amount || 0;
            });

            return [{ amount }];
        } catch (e) {
            return [{ amount: 0 }];
        }
    };

    render() {
        const { totalAmount, pageSize, company } = this.props;

        const isCustomerInvoiceNumberingActive = isCompanyFeatureActive(
            company,
            CompanyFeatures.CUSTOMER_INVOICE_NUMBERING
        );

        let columnDefs = Object.values(allGridColumns);
        if (!isCustomerInvoiceNumberingActive) {
            columnDefs = columnDefs.map(def => {
                if (def.field === "number") {
                    def.type = "numericColumn";
                    def.filter = "agNumberColumnFilter";
                }

                return def;
            });
        }

        return (
            <>
                <div className="clear bm-10">
                    {totalAmount > 0 && (
                        <Header floated="left" as="h4">
                            Total: {formatAmount(totalAmount)}
                        </Header>
                    )}

                    <GridPageSizePicker
                        button
                        compact
                        pointing="top"
                        pageSize={pageSize}
                        text={`${pageSize}/page`}
                        className="right floated basic"
                        onChange={this.props.changePaymentGridState}
                    />

                    <Button
                        size="mini"
                        icon="download"
                        floated="right"
                        content="Export"
                        onClick={() =>
                            this.grid.api.exportDataAsCsv({
                                fileName: `payments_report_${Date.now()}`,
                                // columnKeys: without(shownColumns, 'actions')
                            })
                        }
                    />

                    <DatesRangeInput
                        size="mini"
                        className="fr"
                        clearable={true}
                        animation={null}
                        maxDate={new Date()}
                        placeholder="From - To"
                        value={this.state.dateRange}
                        dateFormat={DateFormat.toUpperCase()}
                        onChange={this.handleDateRangeChange}
                    />
                </div>
                <div className="ag-theme-balham bm-10">
                    <AgGridReact
                        pagination
                        paginationPageSize={this.props.pageSize}
                        rowHeight={56}
                        rowSelection="single"
                        domLayout="autoHeight"
                        rowModelType="infinite"
                        onGridReady={this.onGridReady}
                        cacheBlockSize={this.props.pageSize}
                        onPaginationChanged={this.changePage}
                        columnDefs={columnDefs}
                        noRowsOverlayComponent={NoPaymentsOverlay}
                        defaultColDef={{
                            resizable: true,
                            sortable: true,
                            floatingFilter: true,
                            autoHeight: true,
                        }}
                        pinnedBottomRowData={this.getFooterData()}
                    />
                </div>
            </>
        );
    }
}

const mapStateToProps = ({ invoicePaymentGrid, auth }) => ({
    payments: invoicePaymentGrid.payments,
    pageSize: invoicePaymentGrid.pageSize,
    isLoading: invoicePaymentGrid.isLoading,
    sortModel: invoicePaymentGrid.sortModel,
    columnDefs: invoicePaymentGrid.columnDefs,
    filterModel: invoicePaymentGrid.filterModel,
    totalAmount: invoicePaymentGrid.totalAmount,
    currentPage: invoicePaymentGrid.currentPage,
    company: auth.user.company,
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            loadPaymentGridPage,
            changePaymentGridState,
            changePaymentGridPage: page => dispatch(changePaymentGridPage(page)),
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(DataGrid);
